import { makeAutoObservable, reaction, type IReactionDisposer, runInAction } from 'mobx'
import { logger } from 'shared/lib'
import { TableStore } from 'shared/ui/TableNew'
import { RequestCancelAxios } from 'shared/api/lib'
import { ContactsApi } from 'entities/Contacts'
import type { IResponseContactUnlinked } from 'entities/Contacts/api/types'
import type { IEntitiesStore } from 'entities/EntitiesExport'
import { RowContactUnlinked } from 'entities/Contacts/model/Table/RowContactUnlinked'
import { LinkContactStore } from 'pages/contacts/pages/cleanup/ui/ContactsUnlinkedTable/ui/LinkContact'

export class ContactsUnlinkedTableStore {
  private _itemsMap: Map<number, RowContactUnlinked> = new Map()
  private _disposePagination: IReactionDisposer | null = null
  private readonly _linkContactStore: LinkContactStore
  private readonly _tableStore: TableStore<RowContactUnlinked>
  private _cancelRequest = new RequestCancelAxios()
  private _total = 0

  constructor(private _entities: IEntitiesStore) {
    makeAutoObservable(this)

    this._linkContactStore = new LinkContactStore(this._entities)
    this._linkContactStore.setConfig({
      load: () => {
        this.load()
      },
    })
    this._tableStore = new TableStore({
      key: 'contacts_unlinked',
      limit: 10,
    })
  }

  get tableStore() {
    return this._tableStore
  }

  get linkContactStore() {
    return this._linkContactStore
  }

  get loading() {
    return this._tableStore.loading
  }

  get total() {
    return this._total
  }

  get entities() {
    return this._entities
  }

  init = () => {
    this.reactionPagination()
    this.load()
  }

  dispose = () => {
    this._tableStore.dispose()
    this._itemsMap.clear()
    this._disposePagination?.()
    this._tableStore.setPage(1)
    this._tableStore.setLimit(10)
    this._cancelRequest.reset()
  }

  get params() {
    return this._tableStore.params
  }

  load = async () => {
    if (this.loading) return

    try {
      this._cancelRequest.init()
      this._tableStore.setLoading(true)

      const { data } = await ContactsApi.getContactsUnlinked(this.params, {
        cancelToken: this._cancelRequest.token,
      })

      const results = data.data || []
      const total = data.total || 0

      this.addItems(results)

      runInAction(() => {
        this._total = total
        this._tableStore.setTotal(total)
      })
    } catch (e) {
      logger.error(e)
    } finally {
      this._tableStore.setLoading(false)
    }
  }

  get list() {
    return Array.from(this._itemsMap.values())
  }

  addItems = (contacts: IResponseContactUnlinked[]) => {
    this._itemsMap.clear()

    contacts.forEach((item) => {
      this._itemsMap.set(item.id, new RowContactUnlinked(item))
    })
  }

  reactionPagination = () => {
    this._disposePagination?.()
    this._disposePagination = reaction(
      () => this._tableStore.params,
      () => {
        this.load()
      }
    )
  }
}
